<template>
  <LineChart
    :request-status="requestStatus"
    :series="series"
    :categories="categories"
    :title="title"
    :item-id="itemId"
    class="make-a-copy"
    :card-options="cardOptions"
    v-bind="$attrs"
    @reload="getData"
  />
</template>

<script>
import LineChart from '@/components/cards/models/LineChart';
import cardsModel from '@/mixins/cards-model';
import { ENTITIES, OPERATION, VALUE_TYPES } from '@/services/charts-service';
import { DATA_TYPE } from '@/store/modules/cards/constants';
import colors from '@/utils/colors';
import { msToTime } from '@/utils/date';
import EventBus from '@/utils/event-bus';
import { mapActions } from 'vuex';

export default {
  name: 'SumPostsAndInteractionsInTime',
  components: {
    LineChart,
  },
  mixins: [
    cardsModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    metric: {
      type: [String, Array],
      default: null,
    },
    entity: {
      type: String,
      default: ENTITIES.POSTS,
    },
    label: {
      type: String,
      default: null,
    },
    operation: {
      type: String,
      default: OPERATION.COUNT,
    },
    valueType: {
      type: String,
      default: null,
    },
    ownerPosts: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      rawSeries: [],
      classifiedPercentage: 0,
      cardOptions: {
        chart: {
          type: 'areaspline',
        },
        plotOptions: {
          areaspline: {
            lineColor: colors.componentsColor.seriesLineColor,
            lineWidth: 1,
            fillColor: {
              linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
              stops: [
                [0, colors.componentsColor.seriesAreaColorStart],
                [1, colors.componentsColor.seriesAreaColorEnd],
              ],
            },
            marker: {
              fillColor: colors.componentsColor.seriesLineColor,
              radius: 2,
            },
          },
        },
        legend: false,
        ...(
          this.valueType === VALUE_TYPES.TIME
            ? {
              yAxis: {
                type: 'datetime',
                labels: {
                  formatter() {
                    return msToTime(this.value);
                  },
                },
              },
              tooltip: {
                pointFormatter() {
                  const { y, color, series } = this;
                  const value = msToTime(y);
                  return `<span style="color:${color}">●</span> ${series.name}: <b>${value}</b><br/>`;
                },
              },
            }
            : null
        ),
      },
    };
  },
  computed: {
    formattedData() {
      return this.rawSeries?.points?.map(({ x, z: y }) => ({ x, y }));
    },
    series() {
      return [{
        data: this.isQuarterOnDatePortion ? this.parseData(this.formattedData) : this.formattedData,
        name: this.label || this.title,
      }];
    },
    categories() {
      if (!this.isQuarterOnDatePortion) {
        return null;
      }

      return this.formattedData?.map(({ x }) => this.formattedLabelForQuarter(x));
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getChartsService',
    ]),
    getData(options) {
      let fields;

      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      if (this.metric) {
        fields = [this.metric];
      }

      const promises = [
        this.getChartsService({
          itemId: this.itemId,
          method: 'getTimeSeries',
          points_parser: 'points',
          entity: this.entity,
          filterType: DATA_TYPE.LISTENING,
          params: {
            fields,
            operators: this.operation,
            datePortion: this.getDatePortion(options),
          },
        }),
        this.getChartsService({
          itemId: this.itemId,
          method: 'getTimeSeries',
          points_parser: 'points',
          entity: this.entity,
          filterType: DATA_TYPE.SAC,
          params: {
            fields,
            operators: this.operation,
            datePortion: this.getDatePortion(options),
          },
        }),
      ];

      Promise.all(promises)
        .then(([resListening, resSac]) => {
          const seriesSum = this.aggregateChartResponses(resListening, resSac);
          this.rawSeries = seriesSum;
          this.requestStatus = 'success';

          if (options?.silentRefresh) {
            EventBus.$emit('updated', { itemId: this.itemId });
          }
        }).catch(error => {
          console.error(error);
          this.requestStatus = 'error';
        });
    },
    parseData(data) {
      return this.isQuarterOnDatePortion ? data?.map(({ y }) => y) : data?.data;
    },
    /**
     * @summary Soma as respostas de dois gráficos
     *
     * x: data no gráfico, z: valor no gráfico, name: method. Ex: 'counter'
     * @typedef {{x: number, z: number, name: string}} chartPoint
     * @typedef {{axes: {}, points: chartPoint[]}} chartResponse
     * @param {chartResponse} chartRes1
     * @param {chartResponse} chartRes2
     * @returns {chartResponse}
     */
    aggregateChartResponses(chartRes1, chartRes2) {
      /** @type {chartPoint[]} */
      const result = [];
      const pointsMap = new Map();

      chartRes1.points.forEach(p1 => pointsMap.set(p1.x, p1.z));
      chartRes2.points.forEach(p2 => pointsMap.set(p2.x, (pointsMap.get(p2.x) || 0) + p2.z));

      for (const key of pointsMap.keys()) {
        result.push({ x: key, z: pointsMap.get(key), name: 'counter' });
      }
      return { axes: chartRes1.axes, points: result };
    },
  },
};
</script>
